import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { Provenance } from './types';

/**
 * The catalog layer of the Products page. Registry records join to catalog
 * System entities by univerus.com/product-code; where the catalog has a value
 * it wins, and the curated registry file only fills the gaps.
 */
export const CATALOG_ANNOTATIONS = {
  productCode: 'univerus.com/product-code',
  brand: 'univerus.com/brand',
  website: 'univerus.com/website',
  contact: 'univerus.com/contact',
} as const;

/** Product codes compare trimmed and case-insensitively. */
export const normalizeCode = (code?: string | null) =>
  code?.trim().toLowerCase() || undefined;

/** A trimmed annotation value, or undefined when blank. */
export const annotation = (
  entity: Entity | undefined,
  key: string,
): string | undefined =>
  entity?.metadata.annotations?.[key]?.trim() || undefined;

export const isSystem = (entity: Entity) =>
  entity.kind.toLowerCase() === 'system';

/** Group for catalog-only products whose Domain is not known. */
export const CATALOG_PORTFOLIO = {
  id: 'catalog',
  name: 'From the catalog',
} as const;

/** Catalog Systems by product code; the first by entity ref wins a tie. */
export function systemsByCode(entities: Entity[]): Map<string, Entity> {
  const index = new Map<string, Entity>();
  const systems = entities
    .filter(isSystem)
    .sort((a, b) => stringifyEntityRef(a).localeCompare(stringifyEntityRef(b)));
  for (const entity of systems) {
    const code = normalizeCode(
      annotation(entity, CATALOG_ANNOTATIONS.productCode),
    );
    if (code && !index.has(code)) index.set(code, entity);
  }
  return index;
}

export const catalogProvenance = (entity: Entity): Provenance => ({
  source: `Catalog · ${stringifyEntityRef(entity)}`,
  layer: 'catalog',
});

export const curatedProvenance = (record?: Provenance): Provenance => ({
  ...record,
  source: record?.source || 'Product registry',
  layer: 'curated',
});

/**
 * One joined value: the catalog's when it has one, otherwise the curated
 * value. Provenance is recorded only for values that exist.
 */
export function joinField<T>(
  catalog: T | undefined,
  curated: T | undefined,
  entity: Entity | undefined,
  record: Provenance | undefined,
): { value: T | undefined; provenance?: Provenance } {
  if (entity && catalog !== undefined && catalog !== null && catalog !== '')
    return { value: catalog, provenance: catalogProvenance(entity) };
  if (curated !== undefined && curated !== null && curated !== '')
    return { value: curated, provenance: curatedProvenance(record) };
  return { value: undefined };
}
